<template>
  <section
    :id="ANCHORS.TIP"
    class="useful_tip"
  >
    <div class="osk-wrapper">
      <div class="title_block_sm">
        {{ $t('components.info.usefulTips.title') }}
      </div>

      <div class="flex_block_tip">
        <div
          v-for="tip in tips"
          :key="tip.title"
          class="block"
        >
          <div class="icon">
            <img
              :src="tip.icon"
              alt="logo"
            >
          </div>

          <div class="name_block">
            {{ tip.title }}
          </div>

          <p>{{ tip.description }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { filename } from 'pathe/utils';

import { ANCHORS } from '~/components/info/anchors';

const glob: Record<string, { default: string }> = import.meta.glob('~/assets/img/tips/*.svg', { eager: true });
const icons = Object.fromEntries(
  Object.entries(glob).map(([ key, value ]) => [ filename(key), value.default ]),
);

const props = withDefaults(defineProps<{
  type: 'sell' | 'buy'
}>(), {});

const { isInternational } = useInternational();
const { t } = useI18n();

const sellTips = [
  {
    title: t('components.info.usefulTips.sellTips1.title'),
    description: t('components.info.usefulTips.sellTips1.description'),
    icon: icons.bell,
  },
  {
    title: t('components.info.usefulTips.sellTips2.title'),
    description: t('components.info.usefulTips.sellTips2.description'),
    icon: icons['user-comments'],
  },
  {
    title: t('components.info.usefulTips.sellTips3.title'),
    description: t('components.info.usefulTips.sellTips3.description'),
    icon: icons.hanger,
  },
  {
    title: t('components.info.usefulTips.sellTips4.title'),
    description: t('components.info.usefulTips.sellTips4.description'),
    icon: icons.dispute,
  },
];
const buyTips = [
  {
    title: t('components.info.usefulTips.buyTips1.title'),
    description: t('components.info.usefulTips.buyTips1.description'),
    icon: icons.filter,
  },
  {
    title: t('components.info.usefulTips.buyTips2.title'),
    description: t('components.info.usefulTips.buyTips2.description'),
    icon: icons.hanger,
  },
  {
    title: t('components.info.usefulTips.buyTips3.title'),
    description: t('components.info.usefulTips.buyTips3.description'),
    icon: icons['star-medal'],
  },
  {
    title: t('components.info.usefulTips.buyTips4.title'),
    description: '',
    icon: icons.heart,
  },
  {
    title: t('components.info.usefulTips.buyTips5.title'),
    description: t('components.info.usefulTips.buyTips5.description'),
    icon: isInternational ? icons['price-decrease-int'] : icons['price-decrease'],
  },
  {
    title: t('components.info.usefulTips.buyTips6.title'),
    description: t('components.info.usefulTips.buyTips6.description'),
    icon: icons.dispute,
  },
];
const tips = {
  buy: buyTips,
  sell: sellTips,
}[props.type];
</script>

<style scoped lang="scss">
section.useful_tip {
  padding: 100px 0 63px;
  border-top: 1px solid rgba(51, 51, 51, 0.1);
}

section.useful_tip.useful_tip_buyer {
  padding: 81px 0 71px;
}

section.useful_tip.useful_tip_buyer .flex_block_tip .block {
  margin-bottom: 48px;
}

section.useful_tip .wrapper {
  max-width: 1120px;
}

section.useful_tip .flex_block_tip {
  margin-top: 47px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}

section.useful_tip .flex_block_tip .block {
  width: calc(50% - 10px);
  box-sizing: border-box;
  padding-right: 25px;
  margin-bottom: 60px;
}

section.useful_tip .flex_block_tip .block .icon {
  height: 25px;
  position: relative;
}

section.useful_tip .flex_block_tip .block .icon svg {
  position: absolute;
  top: 50%;
  left: 0;
  transform: translate(0, -50%);
}

section.useful_tip .flex_block_tip .block .name_block {
  font-size: 16px;
  line-height: 18px;
  font-family: "Open Sans", sans-serif;
  font-weight: 500;
  margin-top: 15px;
}

section.useful_tip .flex_block_tip .block p {
  margin-top: 10px;
  font-size: 14px;
  line-height: 21px;
  color: #828282;
}

@media only screen and (min-width: 1024px) {
  section.useful_tip .flex_block_tip .block p {
    max-width: 450px;
  }
}

@media only screen and (max-width: 1024px) {
  section.useful_tip {
    padding: 58px 0 30px;
  }

  section.useful_tip .flex_block_tip .block {
    width: calc(50% - 15px);
    padding-right: 0;
    margin-bottom: 40px;
  }

  section.useful_tip .flex_block_tip .block p br {
    display: none;
  }

  section.useful_tip.useful_tip_buyer {
    padding: 58px 0 44px;
  }

  section.useful_tip.useful_tip_buyer .flex_block_tip .block {
    margin-bottom: 28px;
  }
}

@media only screen and (max-width: 767px) {
  section.useful_tip {
    padding: 48px 0 24px;
  }

  section.useful_tip .flex_block_tip {
    display: block;
    margin-top: 30px;
  }

  section.useful_tip .flex_block_tip .block {
    width: 100%;
    margin-bottom: 27px;
  }

  section.useful_tip .flex_block_tip .block .name_block {
    font-size: 14px;
  }

  section.useful_tip .flex_block_tip .block p {
    font-size: 12px;
    line-height: 18px;
  }

  section.useful_tip.useful_tip_buyer {
    padding: 43px 0 44px;
  }

  section.useful_tip.useful_tip_buyer .flex_block_tip .block p {
    margin-top: 4px;
  }
}
</style>
